import {ExperienceSortOrder} from './sortExperiences'

export {ExperienceSortOrder}

export enum ExperiencesDisplayMode {
  All = 'all',
  Custom = 'custom',
  ByLocation = 'byLocation',
}

export enum ExperienceStatusFilter {
  UpcomingAndPast = 'upcomingAndPast',
  Upcoming = 'upcoming',
  Past = 'past',
}

export interface ExperiencesFilterConfig {
  displayMode: ExperiencesDisplayMode
  sortOrder: ExperienceSortOrder
  statusFilter: ExperienceStatusFilter
  enabledLocationIds?: string[]
  enabledExperienceIds?: string[]
  manualOrder?: string[]
}

interface LegacyFilterConfig {
  showAllLocations?: boolean
  enabledLocationIds?: string[]
  showAllExperiences?: boolean
  enabledExperienceIds?: string[]
}

export const DEFAULT_FILTER_CONFIG: ExperiencesFilterConfig = {
  displayMode: ExperiencesDisplayMode.All,
  sortOrder: ExperienceSortOrder.Alphabetically,
  statusFilter: ExperienceStatusFilter.UpcomingAndPast,
}

const asStringArray = (value: unknown): string[] | undefined =>
  Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string')
    : undefined

const migrateLegacyConfig = (legacy: LegacyFilterConfig): ExperiencesFilterConfig => {
  let displayMode = ExperiencesDisplayMode.All

  if (legacy.showAllExperiences === false) {
    displayMode = ExperiencesDisplayMode.Custom
  } else if (legacy.showAllLocations === false) {
    displayMode = ExperiencesDisplayMode.ByLocation
  }

  return {
    displayMode,
    sortOrder: ExperienceSortOrder.Alphabetically,
    statusFilter: ExperienceStatusFilter.UpcomingAndPast,
    enabledLocationIds: asStringArray(legacy.enabledLocationIds),
    enabledExperienceIds: asStringArray(legacy.enabledExperienceIds),
  }
}

const asEnumValue = <T extends string>(value: unknown, allowed: T[], fallback: T): T =>
  allowed.includes(value as T) ? (value as T) : fallback

const sanitizeConfig = (parsed: Record<string, unknown>): ExperiencesFilterConfig => ({
  displayMode: asEnumValue(
    parsed.displayMode,
    Object.values(ExperiencesDisplayMode),
    DEFAULT_FILTER_CONFIG.displayMode,
  ),
  sortOrder: asEnumValue(
    parsed.sortOrder,
    Object.values(ExperienceSortOrder),
    DEFAULT_FILTER_CONFIG.sortOrder,
  ),
  statusFilter: asEnumValue(
    parsed.statusFilter,
    Object.values(ExperienceStatusFilter),
    DEFAULT_FILTER_CONFIG.statusFilter,
  ),
  enabledLocationIds: asStringArray(parsed.enabledLocationIds),
  enabledExperienceIds: asStringArray(parsed.enabledExperienceIds),
  manualOrder: asStringArray(parsed.manualOrder),
})

const isLegacyConfig = (config: any): config is LegacyFilterConfig =>
  'showAllLocations' in config || 'showAllExperiences' in config

export const parseFilterConfig = (value?: string): ExperiencesFilterConfig => {
  if (!value) {
    return DEFAULT_FILTER_CONFIG
  }

  try {
    const parsed = JSON.parse(value)

    if (!parsed || typeof parsed !== 'object') {
      return DEFAULT_FILTER_CONFIG
    }

    if (isLegacyConfig(parsed)) {
      return migrateLegacyConfig(parsed)
    }

    return sanitizeConfig(parsed)
  } catch {
    return DEFAULT_FILTER_CONFIG
  }
}
